/**
 * CallHistory.tsx
 * Talkroute
 *
 * Created by Vel Genov on 12/21/18
 * Copyright (c) 2018 Talkroute. All right reserved.
 */
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  memo,
} from "react";
import { useDispatch } from "react-redux";
import { Dimmer, Loader, Grid } from "semantic-ui-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { domIds } from "@constants/testConstants";
import uiString from "@constants/uiString";
import routes from "@constants/routes";
import {
  fetchCallHistory,
  showCallDetails,
  hideCallDetails,
  callHistoryFilterHelper,
  clearMissedCount,
} from "@actions/callHistoryActions";
import { clearErrors } from "@actions/errorActions";
import validateArray from "@helpers/arrayValidator";
import CallHistoryLineItem from "./CallHistoryLineItem";
import CallHistoryDetails from "./CallHistoryDetails";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import { CallHistoryData } from "@services/dataClasses";
import { useGetAllContactsQuery } from "@services/kingcobraApi";
import { GET_TEN_THOUSAND_CONTACTS, CONTACTS_INITIAL_PAGE } from "@components/Contacts/constants";
import { addContactNameToCallHistory } from "@helpers/functions/contacts";
import { updateUserPreferences } from "@actions/userActions";
import { IUserPreferences } from "@models/Account.models";
import moment from "moment";

// Enables a vertical scrollbar when content overflows from the element
// Part of a "hover to show scrollbar" implementation that's not currently used
const overflowAuto = { overflowY: "auto" as any };

// Enables a vertical scrollbar for the Call History line item display, when content overflows
// Hides the horizontal scrollbar at all times
// The element needs to have a specific height set, in order to show a scrollbar
const callHistoryItemsStyle = {
  overflowY: "auto" as any,
  overflowX: "hidden" as any,
  borderRight: "1px solid var(--color-dark-grey)",
  height: "calc(100% - 60px)",
  backgroundColor: "var(--color-background-grey)",
};

type ICallHistoryListProps = {
  callHistory: CallHistoryData[];
  selectedCallId: number | undefined;
  onShowDetails: (call: CallHistoryData) => any;
};

const CallHistoryList = memo(
  ({ callHistory, selectedCallId, onShowDetails }: ICallHistoryListProps) => (
    <>
      {callHistory.map((entry) => {
        return (
          <CallHistoryLineItem
            key={entry.id}
            call={entry}
            showDetailsCallback={onShowDetails}
            selected={selectedCallId === entry.id}
          />
        );
      })}
    </>
  )
);

/**
 * Displays the current user's call history
 */
const CallHistory: FC = () => {
  const [hover, setHover] = useState(false);
  const callHistory = useAppSelector((state) => state.callHistory.data);
  const dataState = useAppSelector((state) => state.callHistory.dataState);
  const callDetails = useAppSelector((state) => state.callHistory.callDetails);
  const {data: contacts, refetch, isFetching} = useGetAllContactsQuery({ params: { 
    pageSize: GET_TEN_THOUSAND_CONTACTS,
    page: CONTACTS_INITIAL_PAGE
  } });
  const filterCallhistoryBy = useAppSelector(
    (state) => state.callHistory.filterBy
  );
  const callHistoryPagination = useAppSelector(
    (state) => state.callHistory.callHistoryPagination
  );
  const dispatch = useDispatch();

  const filteredCallHistory = useMemo(
    () => contacts ? addContactNameToCallHistory(contacts, callHistoryFilterHelper(callHistory, filterCallhistoryBy)) : callHistoryFilterHelper(callHistory, filterCallhistoryBy),
    [callHistory, filterCallhistoryBy, contacts]
  );

  /**
   * Displays a lading call history message
   */
  const showLoadingMessage = () => (
    <Dimmer
      id={domIds.CALL_HISTORY_LOADER}
      active
      style={{ height: "100%", zIndex: "5" }}
    >
      <Loader />
    </Dimmer>
  );

  /**
   * Displays a data reload button
   */
  const showReloadButton = () => (
    <button type="button" onClick={loadCallHistory}>
      {" "}
      {uiString.RELOAD}{" "}
    </button>
  );

  /**
   * Display the call history along with filtering options
   *
   * @param filteredCallHistory an optional list of call history. No call history is displayed if it's empty or null
   */
  const showCallHistory = () => {
    const hoverStyle = overflowAuto;

    return (
      <Grid style={{ height: "100%", margin: "0px", flexWrap: "nowrap" }}>
        {/* The right or middle column depending on the visibility of the right sidebar */}
        {/* The column width is determined dynamically based on the sidebar size */}
        <Grid.Column
          className="middle-cell"
          style={{ padding: "0px", minWidth: 285 }}
          stretched
        >
          {/* Show the list of conversations */}
          <div
            id="callHistoryScroll"
            onMouseEnter={() => hoverOn()}
            onMouseLeave={() => hoverOff()}
            style={{ ...callHistoryItemsStyle, ...hoverStyle }}
          >
            <InfiniteScroll
              loader={isFetching ? <h4>Loading...</h4> : " "}
              style={{
                overflowX: "hidden",
              }}
              dataLength={callHistory?.length ?? 0}
              next={() => {
                callHistoryPagination &&
                  dispatch(
                    fetchCallHistory({
                      page: callHistoryPagination.currentPage + 1,
                      background: true,
                    })
                  );
              }}
              hasMore={
                callHistoryPagination
                  ? callHistoryPagination.currentPage <
                    callHistoryPagination.totalPages
                  : true
              }
              scrollableTarget="callHistoryScroll"
              endMessage={null}
            >
              {filteredCallHistory.length ? (
                <CallHistoryList
                  callHistory={filteredCallHistory}
                  onShowDetails={showCallHistoryDetails}
                  selectedCallId={callDetails?.id}
                />
              ) : (
                <h5 style={{ textAlign: "center", margin: "13px" }}>
                  {uiString.EMPTY_CALL_HISTORY}
                </h5>
              )}
            </InfiniteScroll>
          </div>
        </Grid.Column>
        <Grid.Column style={{ padding: "0px", marginTop: -60 }} width={16}>
          <CallHistoryDetails />
        </Grid.Column>
      </Grid>
    );
  };

  /**
   * Display call history for a single call
   *
   * @param call contains details about the caller
   */
  const showCallHistoryDetails = useCallback((call: CallHistoryData | null) => {
    dispatch(showCallDetails(callHistory, call, routes.CALL_HISTORY));
  }, []);

  /**
   * Hides the call history details panel
   */
  const hideCallHistoryDetals = () =>
    dispatch(hideCallDetails(routes.CALL_HISTORY));

  /**
   * Called when the user is hovering over the call history list
   */
  const hoverOn = () => setHover(true);

  /**
   * Called when the user is moving out of the call history list
   */
  const hoverOff = () => setHover(false);

  const defaultTextNumber = useAppSelector((state) => state.preferences.selectedOutgoingTextNumber);
  const defaultCallNumber = useAppSelector((state) => state.preferences.selectedOutgoingPhoneNumber);

  /**
   * A stub function to call a phone number back
   * Currently it logs the phone number to be called
   *
   * @param call contains details about the caller
   */
  const callPhoneNumberBack = (call) => {
    const phoneNumberToCall = call.getCallerPhoneNumber();
  };

  // callHistory
  /**
   * Select the 1st call history item, if available
   */
  const selectFirstCall = () => {
    if (validateArray(filteredCallHistory)) {
      if (callDetails) {
        // If a call is selected, don't change anything
        showCallHistoryDetails(callDetails);
        return;
      }

      const firstCall = filteredCallHistory[0];

      if (firstCall) {
        showCallHistoryDetails(firstCall);
      }
    } else {
      showCallHistoryDetails(null);
    }
  };

  /**
   * Starts loading the call history
   */
  const loadCallHistory = () => {
    dispatch(fetchCallHistory({}));
  };

  useEffect(() => {
    dispatch(clearMissedCount());
    dispatch({ type: 'ENTER_CALL_HISTORY' });
    return () => {
      dispatch({ type: 'LEAVE_CALL_HISTORY' });
    };
  }, [dispatch]);

  /**
   * Lifecycle method called when the component is mounted
   * Used for initialization
   */
  useEffect(() => {
    selectFirstCall();
    return () => {
      dispatch(clearErrors());
    };
  }, []);

  useEffect(() => {
    const selectedCall = callHistory.find(
      (item) => item.id === callDetails?.id
    );
    if (selectedCall) showCallHistoryDetails(selectedCall);
    else selectFirstCall();
  }, [callHistory, callDetails]);

  useEffect(() => {
    const data = {
      "callHistoryLastVisited": moment().unix()
    }
    dispatch(updateUserPreferences(data));
  }, [dispatch]);

  return {
    LOADING: showLoadingMessage(),
    ERROR: showCallHistory(),
    READY: showCallHistory(),
  }[dataState];
};

export default CallHistory;
